import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Avatar,
  ModalBody,
  Heading,
  Flex,
  Form,
  FormLabel,
  FormControl,
  Textarea,
  Button,
  ModalFooter,
  ButtonGroup,
  Icon,
  ModalCloseButton
} from '@homebotapp/hb-react-component-catalog'
import type { ModalProps, SubmitHandler } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { LeavingHomebotCtaButton } from '../LeavingHomebotCta/LeavingHomebotCtaButton'
import { selectScheduleACallUri } from '../../../store/selectors/customerProfile'
import { useSelector } from 'react-redux'

interface DmModalProps extends Omit<ModalProps, 'children' | 'onClose'> {
  name: string
  defaultMessage: string
  onSubmit: SubmitHandler<DmModalFormData>
  avatarSrc?: string
  onClose?: () => void
  scheduleACall?: boolean
}

interface DmModalFormData {
  message: string
}

const MSG = defineMessages({
  heading: {
    id: 'DmModal.heading',
    defaultMessage: 'Send a Message to {name}'
  },
  message: {
    id: 'DmModal.message',
    defaultMessage: 'Message'
  },
  scheduleACall: {
    id: 'DmModal.scheduleACall',
    defaultMessage: 'Schedule a Call'
  },
  send: {
    id: 'DmModal.send',
    defaultMessage: 'Send'
  },
  messagePlaceholder: {
    id: 'DmModal.messagePlaceholder',
    defaultMessage: 'Type your message here'
  }
})

export const DmModal = ({
  isOpen,
  onClose = () => {},
  onSubmit,
  avatarSrc,
  name,
  defaultMessage,
  scheduleACall
}: DmModalProps) => {
  const { formatMessage } = useIntl()
  const scheduleACallUri = useSelector(selectScheduleACallUri)

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          <Flex gap='2.5' alignItems='center'>
            <Avatar size='sm' src={avatarSrc} showBorder={false} />
            <Heading size='sm' m='0'>
              {formatMessage(MSG.heading, { name })}
            </Heading>
          </Flex>
        </ModalHeader>
        <Form<DmModalFormData> onSubmit={onSubmit} defaultValues={{ message: defaultMessage }}>
          <ModalBody py='4'>
            <FormControl>
              <FormLabel srOnly>{formatMessage(MSG.message)}</FormLabel>
              <Textarea
                name='message'
                placeholder={formatMessage(MSG.messagePlaceholder)}
                value={defaultMessage}
                tracking={{
                  guid: '8WfoMVBi3d9BgpZJ9L5Ah1',
                  ui_context: 'dm_modal',
                  descriptor: MSG.message.defaultMessage
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup colorScheme='brand' gap='2' alignItems='center'>
              {scheduleACallUri && (
                <>
                  <LeavingHomebotCtaButton
                    scheduleACall
                    hideIllustration
                    headerText={formatMessage(MSG.scheduleACall)}
                    tracking={{
                      guid: '5vC99EmU3PPpppGddabQf1',
                      ui_context: 'dm_modal',
                      descriptor: 'Schedule a call button clicked'
                    }}
                    ctaButtonSize='sm'
                    ctaButtonVariant='ghost'
                    ctaButtonColorScheme='brand'
                    paddingY={4}
                    ctaLinkUrl={scheduleACallUri}
                  >
                    {formatMessage(MSG.scheduleACall)}
                  </LeavingHomebotCtaButton>
                </>
              )}
              <Button
                type='submit'
                rightIcon={<Icon name='paper-plane' />}
                tracking={{
                  guid: 'jn4VQ25kKm36KTJpqL8ZUv',
                  ui_context: 'dm_modal',
                  descriptor: MSG.send.defaultMessage
                }}
              >
                {formatMessage(MSG.send)}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  )
}
